import React from 'react';
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Bookmark } from '../types/bookmarks';
import { stripHtml, normalizeUrl } from '../utils/bookmarkUtils';
import {
  useHighlightScroll,
  rowPulseHighlight,
} from '../hooks/useHighlightScroll';
import BookmarkActionsMenu from './BookmarkActionsMenu';

interface BookmarkListViewProps {
  bookmarks: Bookmark[];
  onClick?: (bookmark: Bookmark) => void;
  onEditStart?: (bookmarkId: string) => void;
  highlightedBookmarkId?: string | null;
  onHighlightComplete?: () => void;
}

/**
 * List/table view for bookmarks: dense rows with name (as link), short notes snippet
 * in smaller font, and action icons. Maximum scan density.
 */
const BookmarkListView: React.FC<BookmarkListViewProps> = ({
  bookmarks,
  onClick,
  onEditStart,
  highlightedBookmarkId,
  onHighlightComplete,
}) => {
  const { ref: highlightedRowRef } = useHighlightScroll<HTMLTableRowElement>(
    !!highlightedBookmarkId,
    onHighlightComplete,
  );

  return (
    <Table size="small" sx={{ tableLayout: 'fixed' }}>
      <TableBody>
        {bookmarks.map((bookmark) => {
          const notesSnippet = stripHtml(bookmark.bookmark_notes).slice(0, 40);
          const href = normalizeUrl(bookmark.bookmark_url);

          return (
            <TableRow
              key={bookmark.bookmark_id}
              ref={
                highlightedBookmarkId === bookmark.bookmark_id
                  ? highlightedRowRef
                  : undefined
              }
              hover
              onClick={() => onClick?.(bookmark)}
              sx={{
                cursor: onClick ? 'pointer' : undefined,
                ...(highlightedBookmarkId === bookmark.bookmark_id && {
                  animation: `${rowPulseHighlight} 0.9s ease-in-out`,
                }),
              }}
            >
              <TableCell sx={{ py: 0.5, px: 1, border: 'none', width: '40%' }}>
                {href ? (
                  <Link
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {bookmark.bookmark_name}
                  </Link>
                ) : (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 500 }}
                  >
                    {bookmark.bookmark_name}
                  </Typography>
                )}
              </TableCell>
              <TableCell
                sx={{ py: 0.5, px: 1, border: 'none', overflow: 'hidden' }}
              >
                {notesSnippet && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    }}
                  >
                    {notesSnippet}
                  </Typography>
                )}
              </TableCell>
              <TableCell
                sx={{
                  py: 0.5,
                  px: 0.5,
                  border: 'none',
                  width: 64,
                  whiteSpace: 'nowrap',
                }}
              >
                <BookmarkActionsMenu
                  bookmark={bookmark}
                  onEdit={() => onEditStart?.(bookmark.bookmark_id)}
                  iconSize={16}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default BookmarkListView;
